import { openDB } from 'idb';

import { type Sealed, seal, unseal } from '../../shared/crypto';
import type { LocalRecord } from '../../shared/model';

export type LocalSession = {
  userId: string;
  deviceId: string;
  name: string;
  email: string;
  expiresAt: string;
  signedOut?: boolean;
};

export const database = () =>
  openDB('cairn-prototype-v1', 1, {
    upgrade(db) {
      db.createObjectStore('meta');
      db.createObjectStore('records');
    },
  });

export async function metadata<T>(name: string): Promise<T | undefined> {
  return (await database()).get('meta', name);
}

export async function setMetadata(name: string, value: unknown) {
  return (await database()).put('meta', value, name);
}

let ownerSnapshots = new WeakMap<
  CryptoKey,
  Map<string, { epoch: string; records: LocalRecord[] }>
>();

let decrypted = new WeakMap<
  CryptoKey,
  Map<string, { iv: string; data: string; value: LocalRecord }>
>();

async function decodeCached(name: string, encrypted: Sealed, key: CryptoKey) {
  let cache = decrypted.get(key);

  if (!cache) {
    cache = new Map();
    decrypted.set(key, cache);
  }

  const cached = cache.get(name);

  if (cached?.iv === encrypted.iv && cached?.data === encrypted.data) {
    return cached.value;
  }

  const value = await unseal<LocalRecord>(encrypted, key, name);

  cache.set(name, { iv: encrypted.iv, data: encrypted.data, value });

  return value;
}

export async function localRecords(owner: string, key: CryptoKey): Promise<LocalRecord[]> {
  const db = await database(),
    tx = db.transaction(['records', 'meta'], 'readonly');

  const epoch = (await tx.objectStore('meta').get(`recordsEpoch:${owner}`)) ?? '';
  let snapshots = ownerSnapshots.get(key);

  if (!snapshots) {
    snapshots = new Map();
    ownerSnapshots.set(key, snapshots);
  }

  const cached = snapshots.get(owner);

  if (cached && cached.epoch === epoch) {
    await tx.done;

    return cached.records;
  }

  const keys = (await tx.objectStore('records').getAllKeys()).filter(id =>
    String(id).startsWith(`${owner}:`),
  );

  const encrypted = await Promise.all(
    keys.map(async id => ({ id, value: (await tx.objectStore('records').get(id)) as Sealed })),
  );

  await tx.done;

  const records = await Promise.all(
    encrypted.map(row => decodeCached(String(row.id), row.value, key)),
  );

  if (((await db.get('meta', `recordsEpoch:${owner}`)) ?? '') === epoch) {
    snapshots.set(owner, { epoch, records });
  }

  return records;
}

export async function putRecord(owner: string, record: LocalRecord, key: CryptoKey) {
  return putRecords(owner, [record], key);
}

export async function getRecord(
  owner: string,
  id: string,
  key: CryptoKey,
): Promise<LocalRecord | undefined> {
  const name = `${owner}:${id}`;
  const encrypted = (await (await database()).get('records', name)) as Sealed | undefined;

  return encrypted ? decodeCached(name, encrypted, key) : undefined;
}

export async function forgetKey() {
  decrypted = new WeakMap();
  ownerSnapshots = new WeakMap();

  const db = await database(),
    tx = db.transaction('meta', 'readwrite');

  await tx.store.delete('key');
  const session = (await tx.store.get('session')) as LocalSession | undefined;

  if (session) {
    await tx.store.put({ ...session, signedOut: true }, 'session');
  }

  await tx.done;
}

let queue: Promise<unknown> = Promise.resolve();

export function localExclusive<T>(operation: () => Promise<T>): Promise<T> {
  const result = queue.then(() =>
    typeof navigator !== 'undefined' && navigator.locks
      ? navigator.locks.request('cairn-data-write', operation)
      : operation(),
  );

  queue = result.catch(() => {});

  return result;
}

export async function putRecords(owner: string, records: LocalRecord[], key: CryptoKey) {
  const encrypted = await Promise.all(
    records.map(async record => ({
      id: `${owner}:${record.id}`,
      value: await seal(record, key, `${owner}:${record.id}`),
    })),
  );

  const db = await database(),
    tx = db.transaction(['records', 'meta'], 'readwrite');

  const previousEpoch = (await tx.objectStore('meta').get(`recordsEpoch:${owner}`)) ?? '',
    epoch = crypto.randomUUID();

  for (const record of encrypted) {
    await tx.objectStore('records').put(record.value, record.id);
  }

  await tx.objectStore('meta').put(epoch, `recordsEpoch:${owner}`);
  await tx.done;

  const snapshots = ownerSnapshots.get(key),
    snapshot = snapshots?.get(owner);

  if (snapshot && snapshot.epoch === previousEpoch) {
    const changed = new Map(records.map(r => [r.id, r]));

    snapshots!.set(owner, {
      epoch,
      records: [...snapshot.records.filter(r => !changed.has(r.id)), ...records].sort((a, b) =>
        a.id.localeCompare(b.id),
      ),
    });
  } else {
    snapshots?.delete(owner);
  }
}

export async function deleteLocalRecords(owner: string, ids: string[], key: CryptoKey) {
  const db = await database(),
    tx = db.transaction(['records', 'meta'], 'readwrite');

  for (const id of ids) {
    await tx.objectStore('records').delete(`${owner}:${id}`);
  }

  await tx.objectStore('meta').put(crypto.randomUUID(), `recordsEpoch:${owner}`);
  await tx.done;
  ownerSnapshots.get(key)?.delete(owner);
}
